import React from 'react';

const ROLES = [
  'Sales Rep / AE',
  'Solutions Engineer',
  'Sales Enablement',
  'Sales Manager / Director',
  'Product Marketing',
  'Customer Success',
  'Founder / CEO',
  'Other',
];

const COMPANY_SIZES = [
  '1–10',
  '11–50',
  '51–200',
  '201–1,000',
  '1,000+',
];

const USE_CASES = [
  'Demo highlights for prospects',
  'Sales training / onboarding',
  'Product marketing content',
  'Customer success / QBRs',
  'Internal enablement',
  'Other',
];

/**
 * Step 2 of signup: profile fields.
 *
 * Two modes, both rendered from one form:
 *   - Default (new team): collects company name + role + size + use case.
 *     The signup is the admin of a fresh company.
 *   - Invited (`invitedCompanyName` prop is truthy): the company already
 *     exists, so we hide the company-creation fields and show a "Joining
 *     {name}" banner instead. Only fullName + role get collected.
 *
 * The legacy "Join an existing team with a 12-char code" path is gone —
 * joining a team is now invite-only, and invited users arrive via
 * /?invite=TOKEN which drives the invitedCompanyName prop.
 *
 * Can also be rendered in-app as a "complete your profile" modal for
 * users who skipped this step at signup.
 */
export default function SignupStep2({
  form,
  setForm,
  onSubmit,
  onSkip,
  error,
  loading,
  isModal = false,
  invitedCompanyName = null, // when present, hide company-creation fields
}) {
  const isInvited = !!invitedCompanyName;

  return (
    <div className="auth-view">
      <div className="hero">
        <h2>{isModal ? 'Complete your profile' : 'Tell us about you'}</h2>
        <p>This helps us tailor TrailerCast to your team. Takes 30 seconds.</p>
      </div>

      <form className="auth-form" onSubmit={onSubmit}>
        {/* Invited-user banner — makes it obvious they're joining an existing
            company and don't need to pick a name. */}
        {isInvited && (
          <div className="invite-banner">
            <span className="invite-banner-icon"></span>
            <div>
              <strong>Joining {invitedCompanyName}</strong>
              <p>You'll inherit their plan and team library automatically.</p>
            </div>
          </div>
        )}

        <div className="auth-field">
          <label>Full name</label>
          <input
            type="text"
            autoComplete="name"
            placeholder="Jane Smith"
            value={form.fullName}
            onChange={(e) => setForm({ ...form, fullName: e.target.value })}
            required
          />
        </div>

        {/* Role is always collected — useful for both personal analytics
            and telling the admin who they invited ("Head of Sales joined"). */}
        <div className="auth-field">
          <label>Your role{isInvited ? <span className="auth-field-optional"> (optional)</span> : null}</label>
          <select
            value={form.role || ''}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            required={!isInvited}
          >
            <option value="">Select a role…</option>
            {ROLES.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>

        {/* New-team fields — only rendered when the signup isn't joining an
            existing company via invite. */}
        {!isInvited && (
          <>
            <div className="auth-field">
              <label>Company</label>
              <input
                type="text"
                autoComplete="organization"
                placeholder="Acme Corp"
                value={form.companyName}
                onChange={(e) => setForm({ ...form, companyName: e.target.value })}
                required
              />
              <span className="auth-field-hint">
                You&apos;ll be the admin. Invite teammates by email from your Settings once you&apos;re in.
              </span>
            </div>

            <div className="auth-field">
              <label>Company size</label>
              <select
                value={form.companySize || ''}
                onChange={(e) => setForm({ ...form, companySize: e.target.value })}
              >
                <option value="">Select size…</option>
                {COMPANY_SIZES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            <div className="auth-field">
              <label>Primary use case</label>
              <select
                value={form.useCase || ''}
                onChange={(e) => setForm({ ...form, useCase: e.target.value })}
              >
                <option value="">Select use case…</option>
                {USE_CASES.map((u) => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
            </div>
          </>
        )}

        {error && (
          <div className="error-message">
            <span></span> {error}
          </div>
        )}

        <button type="submit" className="generate-btn ready" disabled={loading}>
          <span className="btn-icon"></span>
          <span className="btn-text">{loading ? 'Saving…' : 'Continue to TrailerCast'}</span>
        </button>

        {onSkip && (
          <button type="button" className="auth-toggle" onClick={onSkip}>
            {isModal ? 'Not now' : 'Skip for now'}
          </button>
        )}
      </form>
    </div>
  );
}
